import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";

/**
 * Dynamic <title> / meta updater. Mount once at the router root; it watches
 * pathname + lang and updates document.title, meta[name="description"],
 * canonical link and OG tags on every navigation.
 *
 * SEO targets per route are crafted around high-volume search queries in
 * all three supported languages (en/ru/he).
 */
const BASE_URL = "https://ifollowers.site";

const ROUTE_SEO = {
  "/": {
    en: { title: "iFollowers — Buy Instagram, TikTok, YouTube Followers, Likes & Views",
          description: "Buy real Instagram followers, likes & views. Free followers trial, TikTok, YouTube, Telegram growth. Safe, fast, Bit payments, 24/7 support." },
    ru: { title: "iFollowers — Накрутка подписчиков Instagram, TikTok, YouTube. Бесплатный старт",
          description: "Купить подписчиков Instagram, лайки и просмотры. Бесплатные подписчики, накрутка TikTok, YouTube, Telegram. Оплата Bit, поддержка 24/7." },
    he: { title: "iFollowers — קניית עוקבים לאינסטגרם, טיקטוק, יוטיוב — ניסיון חינם",
          description: "קניית עוקבים אמיתיים לאינסטגרם, לייקים וצפיות. ניסיון חינם, עוקבים לטיקטוק, יוטיוב, טלגרם. תשלום בBit, תמיכה 24/7." },
  },
  "/shop": {
    en: { title: "Shop — Buy Followers, Likes, Views for Instagram, TikTok, YouTube | iFollowers",
          description: "Pick a platform and grow. Cheap Instagram followers, TikTok likes, YouTube subscribers. Instant delivery, Bit & iPoints payments." },
    ru: { title: "Магазин — купить подписчиков и лайки Instagram, TikTok, YouTube | iFollowers",
          description: "Выберите платформу и растите. Дешёвые подписчики Instagram, лайки TikTok, подписчики YouTube. Мгновенная доставка." },
    he: { title: "חנות — קניית עוקבים, לייקים לאינסטגרם, טיקטוק ויוטיוב | iFollowers",
          description: "בחרו פלטפורמה וצמחו. עוקבים זולים לאינסטגרם, לייקים לטיקטוק, מנויי יוטיוב. משלוח מיידי, תשלום Bit." },
  },
  "/faq": {
    en: { title: "FAQ — How to Buy Instagram Followers Safely | iFollowers",
          description: "Frequently asked questions about buying Instagram followers, likes, views and free trials. Safe, secure, no password required." },
    ru: { title: "Вопросы и ответы — Как купить подписчиков Instagram безопасно | iFollowers",
          description: "Часто задаваемые вопросы о покупке подписчиков, лайков и просмотров Instagram. Без пароля, безопасно." },
    he: { title: "שאלות נפוצות — כיצד לקנות עוקבים באינסטגרם בבטחה | iFollowers",
          description: "שאלות ותשובות לגבי קניית עוקבים, לייקים וצפיות באינסטגרם. בטוח, ללא סיסמה." },
  },
  "/contact": {
    en: { title: "Contact Us — 24/7 Support | iFollowers",
          description: "Get in touch with iFollowers support. Fast response, human agents, 24/7. Questions about orders, payments, followers, likes." },
    ru: { title: "Контакты — Поддержка 24/7 | iFollowers",
          description: "Свяжитесь с поддержкой iFollowers. Быстрый ответ, живые агенты, 24/7. Вопросы о заказах, оплате, подписчиках." },
    he: { title: "צור קשר — תמיכה 24/7 | iFollowers",
          description: "צרו קשר עם תמיכת iFollowers. מענה מהיר, נציגים אנושיים, 24/7. שאלות על הזמנות, תשלום, עוקבים." },
  },
  "/login": {
    en: { title: "Sign In — iFollowers",
          description: "Sign in to your iFollowers account to manage orders, claim free followers, and top up your balance." },
    ru: { title: "Вход — iFollowers",
          description: "Войдите в аккаунт iFollowers, чтобы управлять заказами, получить бесплатных подписчиков и пополнить баланс." },
    he: { title: "התחברות — iFollowers",
          description: "התחברו לחשבון iFollowers כדי לנהל הזמנות, לקבל עוקבים חינם ולטעון יתרה." },
  },
  "/terms": {
    en: { title: "Terms of Service — iFollowers", description: "Terms of service for iFollowers — rules, refunds, warranty." },
    ru: { title: "Условия использования — iFollowers", description: "Условия использования iFollowers — правила, возврат, гарантии." },
    he: { title: "תנאי שימוש — iFollowers", description: "תנאי השימוש של iFollowers — חוקים, החזרים, אחריות." },
  },
  "/privacy": {
    en: { title: "Privacy Policy — iFollowers", description: "How iFollowers handles your data. Privacy-first approach." },
    ru: { title: "Политика конфиденциальности — iFollowers", description: "Как iFollowers обрабатывает ваши данные." },
    he: { title: "מדיניות פרטיות — iFollowers", description: "כיצד iFollowers מטפלת במידע שלכם." },
  },
  "/warranty": {
    en: { title: "Warranty — Refill Guarantee | iFollowers", description: "iFollowers refill warranty for followers, likes and views." },
    ru: { title: "Гарантия — долив подписчиков и лайков | iFollowers", description: "Гарантия iFollowers на долив подписчиков, лайков и просмотров." },
    he: { title: "אחריות — מילוי חוזר | iFollowers", description: "אחריות iFollowers למילוי חוזר של עוקבים, לייקים וצפיות." },
  },
};

function upsertMeta(selector, attr, attrValue, content) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    if (attr) el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function findRouteSeo(pathname) {
  if (ROUTE_SEO[pathname]) return ROUTE_SEO[pathname];
  // Match /shop with querystring as /shop
  const base = pathname.split("?")[0];
  if (ROUTE_SEO[base]) return ROUTE_SEO[base];
  return ROUTE_SEO["/"];
}

export default function SEOHead() {
  const location = useLocation();
  const { lang } = useLanguage();

  useEffect(() => {
    const seo = findRouteSeo(location.pathname);
    const { title, description } = seo[lang] || seo.en;
    document.title = title;
    upsertMeta('meta[name="description"]', "name", "description", description);
    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    upsertCanonical(`${BASE_URL}${location.pathname}`);
    // Language attrs
    try {
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === "he" ? "rtl" : "ltr";
    } catch {}
  }, [location.pathname, lang]);

  return null;
}
